import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface ContactDetail {
  id: number;
  title: string;
  icon: string;
  contact_name: string;
  phone_numbers: string[];
  sort_order: number;
  status: boolean;
}

export default function ContactDetails() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ContactDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getContacts();
  }, [page, limit, search]);

  const getContacts = async () => {
    setLoading(true);
    const response = await request.post("contact-details", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setContacts(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 150,
      format: (value) => value,
    },
    // {
    //   id: "contact_name",
    //   label: "Name",
    //   minWidth: 150,
    //   format: (value) => value || "—",
    // },
    {
      id: "phone_numbers",
      label: "Phone",
      minWidth: 220,
      format: (value) => {
        const contacts = Array.isArray(value) ? value : [];
        return contacts.length
          ? contacts.map((contact, i) => (
              <Box key={i} component="span" sx={{ display: "block", color: "#475569" }}>
                {typeof contact === "string" ? contact : `${contact.name} - ${contact.phone}`}
              </Box>
            ))
          : "—";
      },
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/contact-details/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Contact Details"
        showSearch
        searchPlaceholder="Search contacts..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Contact",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/contact-details/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={contacts}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Contact - " + deleteDialog?.title}
          message="Are you sure you want to delete this contact? This action cannot be undone."
          apiEndpoint={"contact-details/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getContacts();
          }}
        />
      </PageWrapper>
    </>
  );
}